/**
 * #1107 — contrato com voucher de desconto: a régua pura que o RS e o EC compartilham.
 *
 * O voucher concede um crédito NOMINAL (N) por competência e cobra na fatura o valor NEGOCIADO
 * (P ≤ N). Validação, desconto e rótulos moram aqui — uma cópia só, espelho de
 * `checar_termos_do_voucher` (`backend/app/schemas/special_contract.py`) e dos lançamentos
 * `voucher_*` do backend (pinado por `test/voucher.test.ts`, que lê o model).
 */

import { formatMoney } from './contractBilling'
import { rotuloDaCompetencia } from './format'

export type AcumuloDoVoucher = 'accumulate' | 'reset'
export type LiberacaoDoVoucher = 'after_payment' | 'at_start_with_grace'

export const ROTULO_DO_ACUMULO: Readonly<Record<AcumuloDoVoucher, string>> = {
  accumulate: 'Acumular',
  reset: 'Reiniciar',
}

export const DESCRICAO_DO_ACUMULO: Readonly<Record<AcumuloDoVoucher, string>> = {
  accumulate: 'o saldo não usado soma ao crédito da competência seguinte',
  reset: 'o saldo não usado deixa de valer na virada da competência',
}

export const ROTULO_DA_LIBERACAO: Readonly<Record<LiberacaoDoVoucher, string>> = {
  after_payment: 'Após o pagamento',
  at_start_with_grace: 'Na abertura, com tolerância',
}

/** Texto da opção por contrato de quitação atrasada (L9). */
export const ROTULO_DA_QUITACAO_ATRASADA = 'Aproveitar pagamento atrasado na competência vigente'

/** Lançamentos do voucher no ledger — espelho de `VOUCHER_REFS_POR_*` do backend. */
export const ROTULO_DO_LANCAMENTO_DO_VOUCHER: Readonly<Record<string, string>> = {
  voucher_grant: 'Crédito do voucher',
  voucher_charge: 'Cobrança do voucher',
  voucher_coverage: 'Consumo coberto pelo voucher',
  voucher_return: 'Estorno devolvido ao voucher',
  voucher_rollover: 'Virada: saldo acumulado',
  voucher_reset: 'Virada: saldo reiniciado',
  voucher_forfeit: 'Fim do voucher: saldo perdido',
  voucher_freeze: 'Saldo congelado (cobrança em aberto)',
  voucher_unfreeze: 'Saldo descongelado',
  voucher_frozen_lost: 'Saldo congelado não retorna',
  voucher_late_payment: 'Quitação atrasada',
}

export function rotuloDoLancamentoDoVoucher(ref: string | null | undefined): string {
  if (!ref) return '—'
  return ROTULO_DO_LANCAMENTO_DO_VOUCHER[ref] ?? ref
}

/** Rótulo para o extrato da CONTA: só os lançamentos do voucher; o resto devolve `null`. */
export function rotuloDoVoucherNoExtrato(ref: string | null | undefined): string | null {
  return ref ? (ROTULO_DO_LANCAMENTO_DO_VOUCHER[ref] ?? null) : null
}

/**
 * O que cada lançamento faz com o saldo DO VOUCHER: "+" entra, "−" sai, "" não move (a cobrança
 * vai à fatura; virada acumulada, congelado perdido e quitação são marcadores).
 */
const SINAL_DO_LANCAMENTO_DO_VOUCHER: Readonly<Record<string, '+' | '−' | ''>> = {
  voucher_grant: '+',
  voucher_return: '+',
  voucher_unfreeze: '+',
  voucher_coverage: '−',
  voucher_reset: '−',
  voucher_forfeit: '−',
  voucher_freeze: '−',
}

export function sinalDoLancamentoDoVoucher(ref: string | null | undefined): '+' | '−' | '' {
  return ref ? (SINAL_DO_LANCAMENTO_DO_VOUCHER[ref] ?? '') : ''
}

/** Teto do ledger: `credit_transactions.amount` é Numeric(10,2). */
export const VALOR_MAXIMO_DO_VOUCHER = 99_999_999.99
export const TOLERANCIA_MAXIMA_EM_DIAS = 365

const numero = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const centavos = (n: number) => Math.round(n * 100)

/**
 * Mais de 2 casas decimais: o backend (`decimal_places=2`) recusaria com 422. Conferido sobre o
 * NÚMERO, não sobre o texto (o input numérico aceita `1e-3`, que não tem ponto nenhum), e de forma
 * EXATA: um valor de 2 casas é o mesmo double que o seu `toFixed(2)` relido — sem tolerância.
 */
const temMaisDeDuasCasas = (v: number | string | null | undefined) => {
  const n = numero(v)
  return n !== null && Number(n.toFixed(2)) !== n
}

/** Desconto conferível: crédito − valor negociado, e o percentual sobre o crédito. */
export function descontoDoVoucher(
  credito: number | string | null | undefined,
  preco: number | string | null | undefined,
): { valor: number; percentual: number } | null {
  const n = numero(credito)
  const p = numero(preco)
  if (n === null || p === null || n <= 0 || p <= 0) return null
  const valorEmCentavos = centavos(n) - centavos(p)
  return {
    valor: valorEmCentavos / 100,
    percentual: Math.round((valorEmCentavos / centavos(n)) * 10_000) / 100,
  }
}

export type TermosDoVoucherNoFormulario = {
  credito: number | string | null | undefined
  preco: number | string | null | undefined
  acumulo: string | null | undefined
  liberacao: string | null | undefined
  tolerancia: number | string | null | undefined
}

export type ErrosDoVoucher = Partial<
  Record<'credito' | 'preco' | 'acumulo' | 'liberacao' | 'tolerancia', string>
>

/** As mesmas recusas do backend, com a frase para o campo. Sem erro = `{}`. */
export function validarTermosDoVoucher(t: TermosDoVoucherNoFormulario): ErrosDoVoucher {
  const erros: ErrosDoVoucher = {}
  const n = numero(t.credito)
  const p = numero(t.preco)
  if (n === null || n <= 0)
    erros.credito = 'Informe o crédito mensal para consumo (maior que zero).'
  else if (n > VALOR_MAXIMO_DO_VOUCHER) erros.credito = 'Crédito acima do valor máximo aceito.'
  else if (temMaisDeDuasCasas(t.credito)) erros.credito = 'Use no máximo 2 casas decimais.'
  if (p === null || p <= 0) erros.preco = 'Informe o valor mensal deste contrato (maior que zero).'
  else if (temMaisDeDuasCasas(t.preco)) erros.preco = 'Use no máximo 2 casas decimais.'
  else if (n !== null && n > 0 && p > n)
    erros.preco =
      'O valor mensal deste contrato não pode ser maior que o crédito mensal para consumo.'
  if (t.acumulo !== 'accumulate' && t.acumulo !== 'reset')
    erros.acumulo = 'Escolha o que acontece com o saldo não usado na virada.'
  if (t.liberacao !== 'after_payment' && t.liberacao !== 'at_start_with_grace')
    erros.liberacao = 'Escolha quando o crédito é liberado.'
  if (t.liberacao === 'at_start_with_grace') {
    const d = numero(t.tolerancia)
    if (d === null || d < 0 || d > TOLERANCIA_MAXIMA_EM_DIAS || !Number.isInteger(d))
      erros.tolerancia = `Informe os dias de tolerância (0 a ${TOLERANCIA_MAXIMA_EM_DIAS}).`
  }
  return erros
}

// ── Vigência no dia de Brasília ──────────────────────────────────────────
//
// A competência do voucher vira à 00:00 de Brasília (A1), e o motor abre a competência quando
// `agora >= start_date`. Um início "01/11" gravado como 00:00 UTC (= 21:00 de 31/10 em Brasília)
// abriria e cobraria OUTUBRO; um fim às 23:59:59 UTC encerraria 3 h antes. Por isso a vigência do
// voucher vai em horário de Brasília — pelo FUSO (`America/Sao_Paulo`, como o `ZoneInfo` do
// backend), nunca por offset cravado: até 2019 havia horário de verão (UTC−02).

const FUSO_DE_BRASILIA = 'America/Sao_Paulo'

const partesEmBrasilia = new Intl.DateTimeFormat('en-US', {
  timeZone: FUSO_DE_BRASILIA,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

/** O relógio de Brasília num instante, lido como se fosse UTC (para medir a diferença). */
function relogioDeBrasiliaComoUtc(ms: number): number {
  const p = Object.fromEntries(
    partesEmBrasilia.formatToParts(new Date(ms)).map((x) => [x.type, Number(x.value)]),
  )
  return Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!, p.second!)
}

/** O instante (ISO, UTC) em que o relógio de Brasília marca `dia` + `hora`. */
function instanteEmBrasilia(dia: string, hora: [number, number, number]): string {
  const [ano, mes, d] = dia.split('-').map(Number)
  const desejado = Date.UTC(ano!, mes! - 1, d!, ...hora)
  // parte de UTC−03 e corrige pela diferença entre o relógio obtido e o desejado (duas voltas
  // bastam: o offset só muda numa transição de horário de verão)
  let ms = desejado + 3 * 3_600_000
  for (let i = 0; i < 2; i++) ms += desejado - relogioDeBrasiliaComoUtc(ms)
  return new Date(ms).toISOString()
}

/** "2026-11-01" → o instante 00:00:00 desse dia em Brasília. */
export function inicioDoDiaEmBrasilia(dia: string): string {
  return instanteEmBrasilia(dia, [0, 0, 0])
}

/** "2026-11-30" → o instante 23:59:59 desse dia em Brasília. */
export function fimDoDiaEmBrasilia(dia: string): string {
  return instanteEmBrasilia(dia, [23, 59, 59])
}

/** O dia (YYYY-MM-DD, para `<input type="date">`) de um instante, em Brasília. */
export function diaEmBrasilia(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: FUSO_DE_BRASILIA })
}

/** O dia de um instante do voucher (vencimento, pagamento) no fuso da competência: Brasília. */
export function formatarDiaDoVoucher(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: FUSO_DE_BRASILIA })
}

/** A competência do voucher vira à 00:00 de Brasília (A1) — mostrada nesse fuso, sempre. */
export function formatarViradaDoVoucher(iso: string): string {
  const d = new Date(iso)
  const dataNoFuso = d.toLocaleDateString('pt-BR', { timeZone: FUSO_DE_BRASILIA })
  const horaNoFuso = d.toLocaleTimeString('pt-BR', {
    timeZone: FUSO_DE_BRASILIA,
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${dataNoFuso} às ${horaNoFuso} (Brasília)`
}

// ── Memória de cálculo em frases ──────────────────────────────────────────

/** Chaves da memória que o backend grava nos lançamentos do voucher (allowlist do EC). */
export type MemoriaDoVoucher = {
  tipo?: string
  competencia?: string
  competencia_do_voucher?: string
  competencia_seguinte?: string
  credito_nominal?: string
  valor_negociado?: string
  desconto?: string
  vencimento?: string
  financiada_por?: string | null
  pago_em?: string
  consumido_na_origem?: string
  credito_liberado?: string
  valor_do_debito?: string
  valor_do_estorno?: string
  saldo_anterior?: string
  saldo_posterior?: string
  saldo_perdido?: string
  valor?: string
  acumulo?: string | null
  congelado_em?: string
  cobranca_competencia?: string
  competencia_origem?: string
  competencia_beneficiada?: string | null
  aplicada?: boolean
  motivo?: string | null
}

/** Por que uma quitação atrasada não financiou a competência vigente (L5–L7). */
export const MOTIVO_DA_QUITACAO_NAO_APLICADA: Readonly<Record<string, string>> = {
  opcao_desligada: 'opção desligada no contrato',
  outra_cobranca_em_atraso: 'há outra cobrança do voucher em atraso',
  competencia_ja_aberta: 'a competência vigente já estava aberta',
  conta_inativa: 'conta de crédito inativa',
  contrato_encerrado: 'contrato encerrado',
}

const MOTIVO_DO_FIM: Readonly<Record<string, string>> = {
  cancelado: 'cancelado',
  vencido: 'fim da vigência',
}

const competencia = (c: string | null | undefined) => (c ? rotuloDaCompetencia(c) : '—')
const dia = (iso: string | null | undefined) => (iso ? formatarDiaDoVoucher(iso) : '—')

/**
 * A memória de um lançamento do voucher em frases, para a linha da fatura (EC) e o extrato do
 * contrato (RS e EC). Memória de outra modalidade (bloco, excedente) devolve `[]`.
 */
export function linhasDaMemoriaDoVoucher(
  calculo: MemoriaDoVoucher | null | undefined,
  currency = 'BRL',
): string[] {
  if (!calculo?.tipo) return []
  const reais = (v: string | number | null | undefined) => formatMoney(Number(v ?? 0), currency)
  const c = calculo
  switch (c.tipo) {
    case 'voucher':
      if (c.financiada_por) {
        return [
          `Financiada pela quitação de ${competencia(c.financiada_por)}, paga em ${dia(c.pago_em)}`,
          `Crédito do voucher: ${reais(c.credito_nominal)}`,
          `Consumido em ${competencia(c.financiada_por)}: ${reais(c.consumido_na_origem)}`,
          `Crédito liberado: ${reais(c.credito_liberado)}`,
        ]
      }
      return [
        `Crédito do voucher: ${reais(c.credito_nominal)}`,
        `Valor negociado: ${reais(c.valor_negociado)}`,
        `Desconto: ${reais(c.desconto)}`,
        ...(c.vencimento ? [`Vencimento: ${dia(c.vencimento)}`] : []),
      ]
    case 'voucher_cobertura':
      return [
        `Consumo de ${reais(c.valor_do_debito)} coberto pelo voucher`,
        `Saldo do voucher: ${reais(c.saldo_anterior)} → ${reais(c.saldo_posterior)}`,
      ]
    case 'voucher_devolucao':
      return [`Estorno de ${reais(c.valor_do_estorno)} devolvido ao voucher`]
    case 'voucher_concessao':
      return [
        `Crédito de ${reais(c.credito_nominal)} concedido para ${competencia(c.competencia)}` +
          (c.financiada_por
            ? ` (financiado pela quitação de ${competencia(c.financiada_por)})`
            : ''),
      ]
    case 'voucher_virada':
      return [
        c.acumulo === 'reset'
          ? `Saldo de ${reais(c.saldo_anterior)} reiniciado na virada para ${competencia(c.competencia_seguinte)}`
          : `Saldo de ${reais(c.saldo_anterior)} transportado para ${competencia(c.competencia_seguinte)}`,
      ]
    case 'voucher_congelamento':
      return [
        `${reais(c.valor)} congelado: cobrança de ${competencia(c.cobranca_competencia)} em aberto`,
      ]
    case 'voucher_descongelamento':
      return [`${reais(c.valor)} descongelado (congelado em ${competencia(c.congelado_em)})`]
    case 'voucher_congelado_perdido':
      return [
        `${reais(c.valor)} congelado não retorna (congelado em ${competencia(c.congelado_em)})`,
      ]
    case 'voucher_fim':
      return [
        `Saldo de ${reais(c.saldo_perdido)} perdido no fim do contrato (${
          MOTIVO_DO_FIM[c.motivo ?? ''] ?? c.motivo ?? 'motivo não registrado'
        })`,
      ]
    case 'voucher_quitacao_atrasada':
      if (c.aplicada) {
        return [
          `Quitação de ${competencia(c.competencia_origem)} financiou ${competencia(
            c.competencia_beneficiada,
          )} (crédito liberado: ${reais(c.credito_liberado)})`,
        ]
      }
      return [
        `Quitação de ${competencia(c.competencia_origem)} registrada sem financiar a competência vigente: ${
          MOTIVO_DA_QUITACAO_NAO_APLICADA[c.motivo ?? ''] ?? c.motivo ?? 'motivo não registrado'
        }`,
      ]
    default:
      return []
  }
}

// ── Formulário ↔ API ─────────────────────────────────────────────────────

/** O estado do formulário do voucher (o que o input guarda: texto). */
export type FormularioDoVoucher = {
  credito: string
  preco: string
  acumulo: AcumuloDoVoucher | ''
  liberacao: LiberacaoDoVoucher | ''
  tolerancia: string
  quitacaoAtrasada: boolean
}

/** Os termos do voucher como a API os grava e devolve (dinheiro em string). */
export type TermosDoVoucherGravados = {
  voucher_credit_amount?: string | null
  voucher_price?: string | null
  voucher_rollover?: AcumuloDoVoucher | null
  voucher_release_policy?: LiberacaoDoVoucher | null
  voucher_grace_days?: number | null
  voucher_late_payment_carry?: boolean | null
}

export type CorpoDoVoucher = {
  voucher_credit_amount: number
  voucher_price: number
  voucher_rollover: AcumuloDoVoucher
  voucher_release_policy: LiberacaoDoVoucher
  voucher_grace_days: number | null
  voucher_late_payment_carry: boolean
}

const texto = (v: string | null | undefined) =>
  v === null || v === undefined ? '' : String(Number(v))

export function formularioDoVoucher(g: TermosDoVoucherGravados): FormularioDoVoucher {
  return {
    credito: texto(g.voucher_credit_amount),
    preco: texto(g.voucher_price),
    acumulo: g.voucher_rollover ?? '',
    liberacao: g.voucher_release_policy ?? '',
    tolerancia:
      g.voucher_grace_days === null || g.voucher_grace_days === undefined
        ? ''
        : String(g.voucher_grace_days),
    quitacaoAtrasada: Boolean(g.voucher_late_payment_carry),
  }
}

export function validarFormularioDoVoucher(f: FormularioDoVoucher): ErrosDoVoucher {
  return validarTermosDoVoucher({
    credito: f.credito,
    preco: f.preco,
    acumulo: f.acumulo,
    liberacao: f.liberacao,
    tolerancia: f.tolerancia,
  })
}

/** O corpo do create. Só chame com o formulário válido (`validarFormularioDoVoucher` vazio). */
export function payloadDoVoucher(f: FormularioDoVoucher): CorpoDoVoucher {
  return {
    voucher_credit_amount: Number(f.credito),
    voucher_price: Number(f.preco),
    voucher_rollover: f.acumulo as AcumuloDoVoucher,
    voucher_release_policy: f.liberacao as LiberacaoDoVoucher,
    voucher_grace_days: f.liberacao === 'at_start_with_grace' ? Number(f.tolerancia) : null,
    voucher_late_payment_carry: f.quitacaoAtrasada,
  }
}

/**
 * O corpo do PUT: só os termos que MUDARAM — todo termo enviado vira revisão, que vale na
 * próxima competência. Sair da liberação na abertura manda `voucher_grace_days: null`.
 */
export function mudancasDoVoucher(
  f: FormularioDoVoucher,
  g: TermosDoVoucherGravados,
): Partial<CorpoDoVoucher> {
  const novo = payloadDoVoucher(f)
  const atual = {
    voucher_credit_amount: g.voucher_credit_amount == null ? null : Number(g.voucher_credit_amount),
    voucher_price: g.voucher_price == null ? null : Number(g.voucher_price),
    voucher_rollover: g.voucher_rollover ?? null,
    voucher_release_policy: g.voucher_release_policy ?? null,
    voucher_grace_days: g.voucher_grace_days ?? null,
    voucher_late_payment_carry: Boolean(g.voucher_late_payment_carry),
  }
  const mudancas: Partial<Record<keyof CorpoDoVoucher, unknown>> = {}
  for (const k of Object.keys(novo) as (keyof CorpoDoVoucher)[]) {
    if (novo[k] !== atual[k]) mudancas[k] = novo[k]
  }
  return mudancas as Partial<CorpoDoVoucher>
}

/**
 * O que uma alteração agendada muda, em frases ("Valor faturado: R$ 750,00"), na ordem do
 * formulário — para o aviso dizer O QUE muda, não repetir crédito e valor que não mudaram.
 */
export function descricaoDasMudancasDoVoucher(
  m: Partial<CorpoDoVoucher>,
  currency = 'BRL',
): string[] {
  const frases: string[] = []
  if (m.voucher_credit_amount !== undefined)
    frases.push(`Crédito: ${formatMoney(m.voucher_credit_amount, currency)}`)
  if (m.voucher_price !== undefined)
    frases.push(`Valor faturado: ${formatMoney(m.voucher_price, currency)}`)
  if (m.voucher_rollover !== undefined)
    frases.push(`Saldo não usado na virada: ${ROTULO_DO_ACUMULO[m.voucher_rollover]}`)
  if (m.voucher_release_policy !== undefined)
    frases.push(`Liberação do crédito: ${ROTULO_DA_LIBERACAO[m.voucher_release_policy]}`)
  if (m.voucher_grace_days !== undefined && m.voucher_grace_days !== null)
    frases.push(`Tolerância: ${m.voucher_grace_days} dias`)
  if (m.voucher_late_payment_carry !== undefined)
    frases.push(
      `Pagamento atrasado: ${m.voucher_late_payment_carry ? 'aproveitado' : 'não aproveitado'} na competência vigente`,
    )
  return frases
}

/**
 * A competência de um lançamento do voucher: cobertura e devolução levam no `competence_period`
 * o mês da FATURA (UTC, para caírem na mesma fatura do débito); o do VOUCHER (Brasília) está na
 * memória. Uma régua para o extrato do RS e o drawer do EC.
 */
export function competenciaDoLancamentoDoVoucher(l: {
  competence_period: string | null
  calculo: MemoriaDoVoucher | null
}): string | null {
  return l.calculo?.competencia_do_voucher ?? l.competence_period
}
